import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import EmployeeAdd from "./EmployeeAdd";
import EmployeeEdit from "./EmployeeEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";

const EmployeeIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // Fetch employee records with search and pagination parameters.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["employee", searchTerm, page, rowsPerPage],
    `shop/employees/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/employees", token);

  // Refetch whenever search, page or rowsPerPage changes.
  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, searchTerm, refetch]);

  // Determine employees list and total count from response (DRF paginated response is expected)
  const employees = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : employees.length;

  if (isError) {
    return <ErrorPage message={t("employee.failedToLoadEmployees")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("employee.employeeList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("employee.addEmployee")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("employee.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      {/* Search Field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("employee.searchEmployees")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Employee Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="employee table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>
                {t("employee.employeeId")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.shop")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.name")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.email")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.contact")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.address")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.salary")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("employee.age")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.contractDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.contractDurations")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("employee.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Shop",
                      "Name",
                      "Email",
                      "Contact",
                      "Address",
                      "Salary",
                      "Age",
                      "Contract Date",
                      "Contract Durations",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : employees.map((employee, index) => (
                  <TableRow key={employee.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {employee?.shop_details?.name ||
                        t("employee.unknownShop")}
                    </TableCell>
                    <TableCell>{employee.name || "--"}</TableCell>
                    <TableCell>{employee.email || "--"}</TableCell>
                    <TableCell>{employee.contact || "--"}</TableCell>
                    <TableCell>{employee.address || "--"}</TableCell>
                    <TableCell>{employee.salary || "--"}</TableCell>
                    <TableCell>{employee.age || "--"}</TableCell>
                    <TableCell>{employee.contract_date || "--"}</TableCell>
                    <TableCell>{employee.contract_durations || "--"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(employee);
                          setOpenEdit(true);
                        }}
                      >
                        {t("employee.editEmployee")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(employee.id)}
                      >
                        {t("employee.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography>{t("employee.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
        />
      </Grid>
      {openAdd && (
        <EmployeeAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <EmployeeEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default EmployeeIndex;
